import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Zap, Map, Route, Wallet, LayoutDashboard, BarChart3, Plug, Clock } from 'lucide-react';

const FEATURES = [
  { icon: Zap, titleKey: 'v2_feature1_title', descKey: 'v2_feature1_desc' },
  { icon: Map, titleKey: 'v2_feature2_title', descKey: 'v2_feature2_desc' },
  { icon: Route, titleKey: 'v2_feature3_title', descKey: 'v2_feature3_desc' },
  { icon: Wallet, titleKey: 'v2_feature4_title', descKey: 'v2_feature4_desc' },
  { icon: LayoutDashboard, titleKey: 'v2_feature5_title', descKey: 'v2_feature5_desc' },
  { icon: BarChart3, titleKey: 'v2_feature6_title', descKey: 'v2_feature6_desc' },
  { icon: Plug, titleKey: 'v2_feature7_title', descKey: 'v2_feature7_desc' },
  { icon: Clock, titleKey: 'v2_feature8_title', descKey: 'v2_feature8_desc' },
];

export const FeaturesV2 = () => {
  const { t } = useTranslation();

  return (
    <section id="features" className="relative py-24 sm:py-32 overflow-hidden" data-testid="v2-features">
      <div className="absolute inset-0">
        <div className="absolute top-1/2 right-0 w-[500px] h-[500px] rounded-full blur-[140px] bg-purple-700/15 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-[120px] bg-orange-600/10" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14 sm:mb-20 max-w-3xl"
        >
          <span className="inline-block px-3 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-700 dark:text-fuchsia-300 text-xs uppercase tracking-widest font-semibold mb-5">
            {t('v2_features_badge')}
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05] tracking-tight">
            {t('v2_features_title')}{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-fuchsia-400 to-orange-400">
              {t('v2_features_title_accent')}
            </span>
          </h2>
          <p className="mt-5 text-foreground/65 text-base sm:text-lg max-w-2xl">
            {t('v2_features_sub')}
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {FEATURES.map((f, idx) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.titleKey}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (idx % 4) * 0.08 }}
                className="group relative p-6 rounded-2xl bg-foreground/[0.04] backdrop-blur border border-border hover:border-fuchsia-500/40 hover:bg-foreground/[0.07] transition-all duration-500"
                data-testid={`v2-feature-${idx}`}
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500/20 to-purple-700/10 border border-fuchsia-500/30 flex items-center justify-center mb-5 group-hover:scale-110 group-hover:shadow-[0_0_24px_rgba(176,38,255,0.45)] transition-all duration-300">
                  <Icon className="w-5 h-5 text-fuchsia-700 dark:text-fuchsia-300" strokeWidth={1.6} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                  {t(f.titleKey)}
                </h3>
                <p className="text-sm text-foreground/65 leading-relaxed">
                  {t(f.descKey)}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
